<script lang="ts" setup>
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import { i18n } from '@/i18n';
import { ref, watch } from 'vue';
import AddOptionSection from './AddOptionSection.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { storeToRefs } from 'pinia';
import { keyMappingForSelectInput, formOptionsBusiness, inputTypeKeyMap } from '../constants';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { objectDiff } from '@/utils/objectDiff';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { UpdateBusinessViewModel } from '../UpdateBusinessViewModel';
import { useUpdateBaseBusinessForm } from '../composables/useUpdateBaseBusinessForm';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();
const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { getAddableOptions } = store;

const props = withDefaults(
  defineProps<{
    item?: IBusiness;
  }>(),
  { item: undefined }
);

const emits = defineEmits<{
  (e: 'close', modal: string, isNext?: boolean): void;
}>();

const {
  fieldState,
  allValidate,
  isEdited,
  addNewOption,
  updateValue,
  updateUserValue,
  initializeForm,
  createUpdatedBaseBusiness,
  currentOpenSelectInputName,
} = useUpdateBaseBusinessForm();

const settingRepository = new SettingRepository();
const baseFreeColumnSettings = ref<Omit<BusinessFreeColumnValue, 'value'>[]>([]);
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);

/**
 * 自由欄設定（関連取引）を取得し、設定済みの項目だけを保持する。columnNo は 1 から採番する。
 */
const fetchBaseFreeColumnSettings = async () => {
  const result = await settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    baseFreeColumnSettings.value = [];
    return;
  }
  baseFreeColumnSettings.value = result.status.data.base
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      target: 'base' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力欄を編集対象の現在値でプリフィルする
 */
const initializeBusinessFreeColumns = () => {
  const stateValues = state.value.item?.businessFreeColumnValues;
  const sourceValues = stateValues?.some((column) => column.target === 'base') ? stateValues : props.item?.businessFreeColumnValues;
  businessFreeColumnValues.value = baseFreeColumnSettings.value.map((setting) => ({
    ...setting,
    value:
      sourceValues?.find((column) => (column.target === 'base' || column.target === undefined) && column.columnNo === setting.columnNo)
        ?.value ?? '',
  }));
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  isEdited.value = true;
};

const baseFreeColumnSettingsReady = fetchBaseFreeColumnSettings();

/**
 * 編集モーダルの保存ボタン押下時の処理
 */
const editingModalOnNext = async () => {
  if (!allValidate()) return;
  const updatedItem = createUpdatedBaseBusiness(props.item);
  updatedItem.businessFreeColumnValues = businessFreeColumnValues.value.map((column) => ({ ...column }));
  const updatedDiff = objectDiff(props.item ?? {}, updatedItem);
  state.value.updatedItemDiff = updatedDiff.first;

  state.value.item = updatedItem;
  emits('close', 'baseBusinessEdit', true);
};

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};

/**
 * モーダルを開いた際の処理
 */
watch(
  () => [state.value.modalState.baseBusinessEdit, state.value.isCreateMode],
  async ([stateNewVal, isCreateModeNewBal]) => {
    if (stateNewVal && !isCreateModeNewBal && props.item) {
      await baseFreeColumnSettingsReady;
      if (!state.value.modalState.baseBusinessEdit || state.value.isCreateMode) return;
      initializeForm(props.item, state.value.item);
      initializeBusinessFreeColumns();
    }
  }
);
</script>
<template>
  <InfoModal
    :modal-state="state.modalState.baseBusinessEdit && !state.isCreateMode"
    :modal-width="1300"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('保存')"
    :is-disabled-next-button="!isEdited"
    @on-back="
      () => {
        emits('close', 'baseBusinessEdit', false);
      }
    "
    @on-next="editingModalOnNext"
  >
    <template #title>{{ t('編集') }}</template>
    <template #description>{{ t('選択済の取引ファイルに対して、項目の内容を修正できます。') }}</template>
    <template #default>
      <div class="modal__container">
        <template v-for="(option, index) in formOptionsBusiness" :key="option">
          <div class="modal__item">
            <template v-if="inputTypeKeyMap[option] === 'selectUser'">
              <MultipleUserSelectInput
                :select-input-index="index"
                :label="t(option)"
                :placeholder="t(option)"
                :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
                @update:model-value="(e) => updateUserValue(option, e)"
              />
            </template>
            <template v-else-if="inputTypeKeyMap[option] === 'select'">
              <SingleSelectInput
                :index="index"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :model-value="fieldState[option].value.value"
                :placeholder="t(option)"
                :filter-type="keyMappingForSelectInput[option]"
                :is-error="fieldState[option]?.isError.value"
                :error-message="fieldState[option]?.errorMessage.value"
                @click:select-input="() => (currentOpenSelectInputName = option)"
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
            <template v-else>
              <TextInput
                :model-value="fieldState[option].value.value"
                :label="t(option) + updateBusinessVM.getRequiredMark(option)"
                :placeholder="t(option)"
                :is-error="fieldState[option].isError.value"
                :error-message="t(fieldState[option].errorMessage.value)"
                ensure-error-msg-height
                @update:model-value="(e) => updateValue(option, e)"
              />
            </template>
          </div>
        </template>
      </div>
      <div v-if="businessFreeColumnValues.length > 0" class="modal__container modal__container--free-column">
        <template v-for="(freeColumn, index) in businessFreeColumnValues" :key="freeColumn.columnNo">
          <div class="modal__item">
            <MultiTagInput
              v-if="freeColumn.isMulti"
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              @editing="markBusinessFreeColumnEdited"
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
            <TextInput
              v-else
              :model-value="freeColumn.value"
              :label="freeColumn.headerName"
              :placeholder="freeColumn.headerName"
              ensure-error-msg-height
              @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            />
          </div>
        </template>
      </div>
      <!-- 新規選択肢追加 -->
      <AddOptionSection :is-aggregate-business="false" :options="getAddableOptions(false)" @set-option="addNewOption" />
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;

    grid-template-columns: repeat(6, 1fr);

    &--free-column {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 100%;
    height: fit-content;

    box-sizing: border-box;
    border-bottom: solid 1px $borderDefault;
  }
}
</style>
